import { useState } from 'react';
import { track } from '@/lib/analytics';

/**
 * The "One Wish Willow" — a hidden, gnarled twig tucked into the footer (a nod
 * to Obsession, 2026). Find it, click it, and it grants your one wish… which the
 * global EasterEggs listener turns into the curse. One wish per visit; then the
 * twig is spent. Reduced-motion-safe.
 */
const WishToy = () => {
    const [stage, setStage] = useState<'idle' | 'granting' | 'spent'>('idle');

    const grant = () => {
        if (stage !== 'idle') return;
        setStage('granting');
        track('wish_made');
        const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
        window.setTimeout(() => {
            window.dispatchEvent(new CustomEvent('mx:wish'));
            setStage('spent');
        }, reduce ? 200 : 1000);
    };

    return (
        <button
            type="button"
            className={`wish-toy wish-toy-${stage}`}
            onClick={grant}
            disabled={stage === 'spent'}
            aria-label="Make a wish"
            title="Make a wish"
        >
            {/* the One Wish Willow — traced from the actual prop (long, twisted) */}
            <svg viewBox="43 83 270 41" fill="currentColor" aria-hidden="true">
                <g transform="translate(0,217) scale(0.1,-0.1)">
                    <path d="M595 1310 c-3 -5 -18 -10 -32 -10 -18 0 -24 -4 -20 -14 3 -8 1 -17 -6 -20 -7 -2 0 -5 16 -5 32 -1 33 -5 7 -31 -15 -15 -18 -25 -12 -38 6 -11 3 -34 -8 -67 -15 -44 -20 -50 -43 -47 -34 4 -54 -29 -31 -52 13 -14 42 -16 170 -15 84 0 157 -2 161 -4 18 -11 368 -28 678 -33 636 -10 821 -14 900 -18 44 -2 218 -2 387 1 l306 6 4 36 c3 20 7 42 9 50 7 24 -51 44 -136 48 -44 2 -95 6 -112 9 -95 15 32 75 163 78 85 1 114 13 114 47 0 11 -7 19 -16 19 -8 0 -21 6 -28 13 -11 10 -122 12 -552 10 -296 -2 -600 -5 -674 -6 -74 -1 -166 3 -205 10 -39 7 -232 15 -434 18 -200 4 -374 11 -388 16 -33 12 -210 12 -218 -1z m148 -107 c-7 -2 -19 -2 -25 0 -7 3 -2 5 12 5 14 0 19 -2 13 -5z m1023 -20 c-3 -21 -48 -28 -113 -18 -66 10 -51 24 30 28 39 1 75 4 79 5 3 1 5 -6 4 -15z m-828 -15 c23 -23 11 -28 -68 -28 -44 0 -80 4 -80 9 0 25 126 41 148 19z m1621 -54 c3 -2 -4 -11 -15 -19 -15 -11 -59 -14 -216 -15 -192 0 -228 3 -228 21 0 22 74 28 260 23 107 -3 197 -8 199 -10z m-822 -54 c22 0 23 -2 9 -15 -8 -8 -23 -15 -33 -15 -15 0 -16 2 -3 10 11 7 3 10 -28 10 -29 0 -41 4 -36 11 4 6 19 9 36 5 17 -3 42 -6 55 -6z" />
                </g>
            </svg>
            <span className="wish-toy-label">
                {stage === 'spent' ? 'wish spent' : stage === 'granting' ? 'granting…' : 'one wish'}
            </span>

            <style>{`
                .wish-toy {
                    position: relative;
                    display: inline-flex;
                    align-items: center;
                    padding: 0.3rem 0.2rem;
                    background: none;
                    border: none;
                    cursor: pointer;
                    vertical-align: middle;
                    color: var(--et-cyan, #2b7d92);
                    opacity: 0.2;
                    transition: opacity 0.45s ease, color 0.45s ease;
                }
                .wish-toy:hover { opacity: 0.9; }
                .wish-toy svg { width: 80px; height: auto; display: block; }
                /* the label floats on top of (above) the twig, centered */
                .wish-toy-label {
                    position: absolute;
                    bottom: calc(100% - 0.1rem);
                    left: 50%;
                    transform: translateX(-50%);
                    font-family: var(--font-mono);
                    font-size: 0.52rem;
                    font-weight: 600;
                    letter-spacing: 0.24em;
                    text-transform: uppercase;
                    opacity: 0;
                    transition: opacity 0.45s ease;
                    white-space: nowrap;
                    pointer-events: none;
                }
                .wish-toy:hover .wish-toy-label { opacity: 0.85; }

                .wish-toy-granting {
                    color: #c4102e;
                    opacity: 1;
                    animation: wish-grant 1s ease both;
                }
                .wish-toy-granting .wish-toy-label { opacity: 1; }
                @keyframes wish-grant {
                    0% { transform: none; filter: none; }
                    15% { transform: translate(-1px, 1px) rotate(-4deg); }
                    30% { transform: translate(2px, -1px) rotate(4deg); }
                    45% { transform: translate(-2px, 0) rotate(-3deg); filter: drop-shadow(0 0 8px rgba(196,16,46,0.8)); }
                    60% { transform: translate(1px, 1px) rotate(3deg); }
                    80% { transform: none; filter: drop-shadow(0 0 14px rgba(196,16,46,0.9)); }
                    100% { transform: none; filter: drop-shadow(0 0 4px rgba(196,16,46,0.5)); }
                }

                .wish-toy-spent {
                    color: #c4102e;
                    opacity: 0.12 !important;
                    pointer-events: none;
                }

                @media (prefers-reduced-motion: reduce) {
                    .wish-toy-granting { animation: none; }
                }
            `}</style>
        </button>
    );
};

export default WishToy;
